/** 
 * © 2025 COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 'CONTPAQi'. Todos los derechos reservados. 
 * Este código fuente constituye una obra original protegida por la legislación nacional e internacional en materia de propiedad intelectual. 
 * Su reproducción, distribución, comunicación pública, transformación, descompilación, ingeniería inversa o cualquier uso no autorizado, 
 * total o parcial, se encuentra expresamente prohibido sin la previa autorización por escrito de COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 
 * El uso indebido de este software puede ser constitutivo de delitos y dará lugar a las responsabilidades civiles y penales correspondientes. 
*/ 
 
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from "@azure/msal-browser";
import { changeTheme } from 'src/utils/themes.utils';
import { connect } from 'react-redux';
import { loginRequest } from "src/config/auth";
import { setThemeUi } from 'src/store/actions/ui.action';
import { TypeOfTheme } from 'src/constants/theme.constant';
import { RoutesSections } from 'src/constants/configuration.constant';
import { BaseAction } from 'src/types/base-action.type';
import { UiState } from 'src/store/reducers/ui.reducer';
import { LocalStorageRepository } from 'src/repositories/local-storage.repository';
import Sidebar from '../organisms/sidebar.component';
import HeaderCustom from '../organisms/header.component';
import { Layout } from 'antd';
import Sider from 'antd/es/layout/Sider';
import { Content, Header } from 'antd/es/layout/layout';
import { DashboardIcon, AgentsIcon, UsersIcon, InfoCircleIcon, DescriptionIcon, CompaniesIcon, OutgoingMailIcon, AgentsFillIcon, GroupIcon, ControlPanelIcon } from '../icons.component';
import MenuSidebar from '../organisms/menu.component';
import Loader from '../atoms/loader.component';
import { updateAuth } from 'src/store/actions/auth.action';
import { AuthRepository } from 'src/repositories/auth.repository';
import { getUserInfo, getUserLicenses } from 'src/store/actions/user.action';

interface ProtectedLayoutProps {
  ui: UiState,
  auth: any,
  setThemeUi(currentTheme: string): BaseAction;
  updateAuth: (data:any) => {},
  getUserLicenses: () => {},
  getUserInfo: () => {},
};

const ProtectedLayout = ({
  ui,
  auth,
  setThemeUi,
  updateAuth,
  getUserLicenses,
  getUserInfo,
} : ProtectedLayoutProps) => {
  //Validate user logged
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const [section, setSection] = useState<string|undefined>();
  const [collapsed, setCollapsed] = useState(false);

  const baseMenu = [
    {
      ...RoutesSections.find(r => r.slug === 'dashboard'),
      icon: <DashboardIcon />
    },
    {
      ...RoutesSections.find(r => r.slug === 'licenses'),
      icon: <DescriptionIcon />
    },
    {
      ...RoutesSections.find(r => r.slug === 'companies'),
      icon: <CompaniesIcon />
    },
    {
      ...RoutesSections.find(r => r.slug === 'users'),
      icon: <GroupIcon />
    },
    {
      ...RoutesSections.find(r => r.slug === 'invitations'),
      icon: <OutgoingMailIcon />
    },
    {
      ...RoutesSections.find(r => r.slug === 'roles'),
      icon: <AgentsFillIcon />
    },
  ];

  const footerMenu = [
    {
      ...RoutesSections.find(r => r.slug === 'help'),
      icon: <InfoCircleIcon />
    }
  ];

  useEffect(() => {
    handleLoginRedirect();
    const theme = LocalStorageRepository.getUiTheme();
    setThemeUi(theme);
    getCurrentSectionName();
  }, []);

  useEffect(() => {
    if (ui.theme === TypeOfTheme.Dark) {
      document.body.classList.add('theme-dark');
    } else {
      document.body.classList.remove('theme-dark');
    }
  }, [ui]);

  useEffect(() => {
    getCurrentSectionName();
  }, [location]);

  useEffect(() => {
    if (isAuthenticated) {
      setAuthData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setAuthData();
  }, [instance, accounts]);

  useEffect(() => {
    if (auth.accessToken) {
      getUserLicenses();
      getUserInfo();
    }
  }, [auth.accessToken]);

  useEffect(() => {
    handleLoginRedirect();
  });

  const getCurrentSectionName = () => {
    const sectionData = RoutesSections.find(r => r.path === location.pathname);
    if (sectionData) {
      setSection(sectionData.label);
    }
  };

  const handleSwithTheme = () => {
    const newTheme = changeTheme();
    setThemeUi(newTheme);
  };

  const handleLoginRedirect = async() => {
    
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      //TODO: Before reintent refresh_token
      console.log("Entro loginRedirect");
      await instance.loginRedirect(loginRequest);
    }
  };

  const handleLogoutRedirec = async() => {
    await instance.logoutRedirect({
      account: instance.getActiveAccount(),
    })
  };

  const setAuthData = async() => {
    let authData = instance.getActiveAccount();
    const request:any = {
      scopes: loginRequest.scopes, // Replace with your required scopes
      account: authData, // Use the first account in the session
    };
    try {
      let msalResponse = await instance.acquireTokenSilent(request);
      if (msalResponse && msalResponse?.idToken) {
        updateAuth(AuthRepository.setAuthentication(authData, msalResponse.idToken));
      } else {
        throw new Error ("Token Error");
      }
    } catch (error:any) {
      console.log("acquireTokenSilent error:", error);
      if (error.name === "InteractionRequiredAuthError") {
        // Fallback to interactive login if silent token acquisition fails
        /*instance.acquireTokenPopup(request).then((response) => {
          console.log("Access Token (Popup):", response.accessToken);
        });*/
      }
    }
  };

  return (
    <>
      {
        isAuthenticated ? (
        <Layout>
          <Sider trigger={null} collapsible collapsed={collapsed}>
            <Sidebar
              isExpanded={!collapsed}
              menu={<MenuSidebar menu={baseMenu} isExpanded={!collapsed} />}
              footer={<MenuSidebar menu={footerMenu} isExpanded={!collapsed} showCollapsible onCollapse={()=>setCollapsed(!collapsed)} />}
            />
          </Sider>
          <Layout>
            <Header>
              <HeaderCustom section={section} onLogout={handleLogoutRedirec} />
            </Header>
            <Content>
              <Outlet />
            </Content>
          </Layout>
        </Layout>
      ) : (
        <Loader fullPage />
      )}
    </>
    
  );
};

const mapDispatchToProps = {
  setThemeUi,
  updateAuth,
  getUserLicenses,
  getUserInfo,
};

const mapStateToProps = (state:any) => ({
  ui: state.ui ?? null,
  auth: state.auth,
});

export default connect(mapStateToProps, mapDispatchToProps)(ProtectedLayout);
